/**
 * This is a RecentlyViewedList Component.This Component is used to render the Recently Viewed List.
 *
 * @module views/Organisms/RecentlyViewedList
 * @memberof -Common
 */
import './RecentlyViewedList.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import Loader from '@ulta/modules/Loader/Loader';

import RecentlyViewedItem from '../RecentlyViewedItem/RecentlyViewedItem';
import * as utils from './RecentlyViewedList';

/**
 * Represents a RecentlyViewedList component
 *
 * @method
 * @param {RecentlyViewedListProps} props - React properties passed from composition
 * @returns RecentlyViewedList
 */
export const RecentlyViewedList = function( props ){
  const {
    handleClearAllRecentlyViewed,
    dataCaptureHandler,
    recentlyViewedAction,
    invokeAction
  } = props;

  const [data, setData] = useState( null );
  const items = data?.items || [];
  const title = data?.title || {};
  const clearRecentlyViewedAction = data?.clearRecentlyViewedAction || {};
  const recentlyViewedIdsRef = useRef( null );

  const handleActionAfter = useCallback(
    utils.composeHandleActionAfter( { setData } ),
    [setData] );

  const handleClear = useCallback(
    utils.composeHandleClear( { recentlyViewedIdsRef }, { setData, handleClearAllRecentlyViewed } ),
    [handleClearAllRecentlyViewed] );

  const handleRecentlyViewedAction = useCallback(
    utils.composeHandleRecentlyViewedAction(
      { recentlyViewedIdsRef, recentlyViewedAction },
      { invokeAction, handleActionAfter } ),
    [recentlyViewedIdsRef, recentlyViewedAction] );

  useEffect( handleRecentlyViewedAction, [handleRecentlyViewedAction] );

  if( !data && recentlyViewedIdsRef.current?.length ){
    return (
      <RecentlyViewedListLoader />
    );
  }

  if( !recentlyViewedIdsRef.current?.length || !items?.length ){
    return null;
  }

  return (
    <div className='RecentlyViewedList'>
      <div className='RecentlyViewedList__header'>
        { title?.text && title?.htmlTag && (
          <Text
            textStyle='body-2'
            htmlTag={ title.htmlTag }
          >
            { title.text }
          </Text>
        ) }
        { clearRecentlyViewedAction?.label && (
          <Button
            className='RecentlyViewedList__clearAll'
            onClick={ handleClear }
            action={ clearRecentlyViewedAction }
            ariaHiddenIcon={ true }
            variant='unstyled'
          >
            <Text
              htmlTag='span'
              textStyle='body-2'
              color='neutral-600'
              textColor='xsubtle'
            >
              { clearRecentlyViewedAction.label }
            </Text>
          </Button>
        ) }
      </div>
      <ul className='RecentlyViewedList__recents'>
        { items?.map( ( item, index ) => (
          <li key={ `recently-viewed-${index}` }>
            <RecentlyViewedItem
              { ...spreadProps( { ...item } ) }
              dataCaptureHandler={ dataCaptureHandler }
            />
          </li>
        )
        ) }
      </ul>
    </div>
  );
};

/**
 * Composes and returns a function to handle recently viewed actions.
 * This function retrieves the recently viewed items from storage and
 * invokes the provided action handler.
 *
 * @param {Object} data - Contains references to the recently viewed data.
 * @param {Object} data.recentlyViewedIdsRef - A ref object to store recently viewed IDs.
 * @param {Object} data.recentlyViewedAction - The action configuration for recently viewed items.
 * @param {Object} methods - Contains the action methods to handle the recently viewed data.
 * @param {Function} methods.handleActionAfter - Function to be executed after invoking the action.
 * @param {Function} methods.invokeAction - Function to trigger the recently viewed action.
 * @returns {Function} A function that retrieves and processes recently viewed items.
 */
export const composeHandleRecentlyViewedAction = ( data, methods ) => () => {
  const { recentlyViewedIdsRef, recentlyViewedAction } = data || {};
  const { handleActionAfter, invokeAction } = methods || {};

  if( !recentlyViewedIdsRef || !recentlyViewedAction || !handleActionAfter ){
    return;
  }

  recentlyViewedIdsRef.current = getStorage( {
    secure: false,
    key: STORAGE_KEY.recentlyViewedSKUsForAutoSuggestion
  } )?.recentlyViewedForAutoSuggest;

  invokeAction( {
    ...recentlyViewedAction,
    after: handleActionAfter
  }
  );
};

/**
 * Composes and returns a function that updates the component state
 * with the provided content after an action is performed.
 *
 * @param {Object} methods - Contains methods for state management.
 * @param {Function} methods.setData - Function to update the component's state.
 * @returns {Function} A function that takes input data and updates the state.
 */
export const composeHandleActionAfter = ( methods ) => ( inputData ) => {
  const { setData } = methods || {};
  const { content } = inputData || {};

  if( !content || !setData ){
    return;
  }

  setData( content );
};

/**
 * Composes and returns a function to handle clearing the recently viewed items.
 * This function clears the recently viewed items from storage, resets the reference,
 * and updates the component state.
 *
 * @param {Object} data - Contains references to the recently viewed data.
 * @param {Object} data.recentlyViewedIdsRef - A ref object that stores recently viewed item IDs.
 * @param {Object} methods - Contains methods to update state and clear storage.
 * @param {Function} methods.setData - Function to update the component's state.
 * @param {Function} methods.handleClearAllRecentlyViewed - Function to remove recently viewed items from storage.
 * @returns {Function} A function that clears recently viewed items and updates state.
 */
export const composeHandleClear = ( data, methods ) => () => {
  const { recentlyViewedIdsRef } = data || {};
  const { setData, handleClearAllRecentlyViewed } = methods || {};

  if( !setData || !handleClearAllRecentlyViewed ){
    return;
  }

  handleClearAllRecentlyViewed();

  recentlyViewedIdsRef.current = [];

  setData( ( oldProps ) => ( {
    ...oldProps,
    items: []
  } ) );
};

/**
 * A functional component that renders a skeleton loader
 * for the Recently Viewed List section while data is loading.
 *
 * This loader displays a placeholder for the title, a "Clear All" button,
 * and a dynamic number of skeleton loaders for individual recently viewed items.
 *
 * @function RecentlyViewedListLoader
 * @param {Object} data - The props passed to the loader component.
 * @returns {JSX.Element} A skeleton loader for the recently viewed list.
 */
export const RecentlyViewedListLoader = ( ) => (
  <div className='RecentlyViewedList__Loader--mainContainer'>
    <div className='RecentlyViewedList__Loader--textContainer'>
      <div className='RecentlyViewedList__Loader--title'>
        <Loader displayType='skeleton'
          size='S'
          multipleLineLoader={ false }
        />
      </div>
      <div className='RecentlyViewedList__Loader--clearAll'>
        <Loader displayType='skeleton'
          size='S'
          multipleLineLoader={ false }
        />
      </div>
    </div>
    <div className='RecentlyViewedList__Loader--cardContainer'>
      { [...Array( 4 )].map( ( _, index ) => (
        <div key={ index }
          className='RecentlyViewedList__Loader--individualCards'
        >
          <Loader displayType='skeleton'
            size='S'
            multipleLineLoader={ false }
          />
        </div>
      ) ) }
    </div>
  </div>
);


/**
 * Defines the prop types for the RecentlyViewedList component.
 *
 * @typedef {Object} RecentlyViewedListProps
 * @property {Function} handleClearAllRecentlyViewed - Function to clear recently viewed items from storage and update state.
 * @property {Function} dataCaptureHandler - Function to capture data events related to recently viewed items.
 * @property {Object} recentlyViewedAction - Configuration for handling recently viewed actions.
 * @property {Function} invokeAction - Function to execute the recently viewed action.
 */
export const propTypes = {
  handleClearAllRecentlyViewed: PropTypes.func.isRequired,
  dataCaptureHandler: PropTypes.func.isRequired,
  recentlyViewedAction: PropTypes.object.isRequired,
  invokeAction: PropTypes.func.isRequired
};

RecentlyViewedList.propTypes = propTypes;

export default RecentlyViewedList;
